import React, { useState, useMemo, useEffect } from 'react';
import { Gamepad2, RotateCcw, Download, Home, Users, Shield, Shirt } from 'lucide-react';
import { playersDatabase, gamesList } from './data';
import { draftFormations, teamEmblems, teamKits } from './draftData';

// Cache global : stocke les URLs déjà lancées pour ne jamais recharger deux fois
const imageCache = new Set();

// Lance le préchargement en arrière-plan (aucun effet visuel)
const preloadImages = (urls) => {
  urls.forEach(url => {
    if (url && !imageCache.has(url)) {
      imageCache.add(url);
      const img = new Image();
      img.src = url;
    }
  });
};

// Composant PlayerAvatar — fond coloré par rôle, sans effet de chargement
const PlayerAvatar = ({ player, size = "w-16 h-16", className = "" }) => {
  const getRoleBackground = (role) => {
    const backgrounds = {
      'GK': '/images/GK.png',
      'DF': '/images/DF.png',
      'MF': '/images/MF.png',
      'FW': '/images/FW.png'
    };
    return backgrounds[role] || backgrounds['MF'];
  };
  
  return (
    <div 
      className={`relative ${size} rounded-full overflow-hidden ${className}`}
      style={{
        backgroundImage: `url(${getRoleBackground(player.role)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <img 
        src={player.sprite} 
        alt={player.name} 
        className="w-full h-full object-cover relative z-10"
        loading="eager"
      />
    </div>
  );
};

const Draft = ({ onImportTeam }) => {
  const [currentMode, setCurrentMode] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentSubStep, setCurrentSubStep] = useState(0);
  const [selectedTeam, setSelectedTeam] = useState({
    coach: null,
    managers: [],
    players: [],
    emblem: null,
    kit: null
  });
  const [currentProposals, setCurrentProposals] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedEmblem, setSelectedEmblem] = useState(null);
  const [selectedKit, setSelectedKit] = useState(null);
  const [selectedFormationTemp, setSelectedFormationTemp] = useState(null); // Formation temporaire avant validation
  const [formationProposals, setFormationProposals] = useState([]); // Propositions de formations
  const [formationRefreshUsed, setFormationRefreshUsed] = useState(false); // Refresh pour formations
  const [refreshUsed, setRefreshUsed] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0); // Compteur pour forcer le rechargement

  // Configuration des étapes selon la formation sélectionnée
  const DRAFT_CONFIG = useMemo(() => {
    if (!selectedFormation) {
      return [
        { position: 'coach', label: '👔 Coach', count: 1 },
        { position: 'manager', label: '📋 Manager', count: 3 },
        { position: 'GK', label: '🥅 Gardien', count: 1 },
        { position: 'DF', label: '🛡️ Défenseur', count: 4 },
        { position: 'MF', label: '⚡ Milieu', count: 4 },
        { position: 'FW', label: '⚔️ Attaquant', count: 2 }
      ];
    }

    const formation = draftFormations[selectedFormation];
    return [
      { position: 'coach', label: '👔 Coach', count: 1 },
      { position: 'manager', label: '📋 Manager', count: 3 },
      { position: 'GK', label: '🥅 Gardien', count: 1 },
      { position: 'DF', label: '🛡️ Défenseur', count: formation.positions.DF },
      { position: 'MF', label: '⚡ Milieu', count: formation.positions.MF },
      { position: 'FW', label: '⚔️ Attaquant', count: formation.positions.FW }
    ];
  }, [selectedFormation]);

  const modeConfig = useMemo(() => {
    if (currentMode === 'classic') {
      return { proposalsCount: 5, maxRefresh: 1, name: 'Classique' };
    } else if (currentMode === 'hardcore') {
      return { proposalsCount: 3, maxRefresh: 0, name: 'Hardcore' };
    } else if (currentMode === 'theme') {
      return { proposalsCount: 5, maxRefresh: 1, name: 'Thème' };
    }
    return { proposalsCount: 5, maxRefresh: 1, name: '' };
  }, [currentMode]);

  const availablePlayers = useMemo(() => {
    if (currentMode === 'theme' && selectedGame && selectedGame !== 'Tous les jeux') {
      return playersDatabase.filter(p => p.game && p.game.includes(selectedGame));
    }
    return playersDatabase;
  }, [currentMode, selectedGame]);

  const availableEmblems = useMemo(() => {
    if (currentMode === 'theme' && selectedGame && selectedGame !== 'Tous les jeux') {
      return teamEmblems.filter(e => e.game === selectedGame);
    }
    return teamEmblems;
  }, [currentMode, selectedGame]);

  const availableKits = useMemo(() => {
    if (currentMode === 'theme' && selectedGame && selectedGame !== 'Tous les jeux') {
      return teamKits.filter(k => k.game === selectedGame);
    }
    return teamKits;
  }, [currentMode, selectedGame]);

  // Précharge les joueurs de la prochaine étape en arrière-plan
  const preloadNextStep = (nextIdx) => {
    if (nextIdx < 0 || nextIdx >= DRAFT_CONFIG.length) return;
    const cfg = DRAFT_CONFIG[nextIdx];
    let pool = cfg.position === 'coach' || cfg.position === 'manager'
      ? availablePlayers
      : availablePlayers.filter(p => p.role === cfg.position);
    const sample = [...pool].sort(() => Math.random() - 0.5).slice(0, 20);
    preloadImages(sample.map(p => p.sprite));
  };

  // Auto-charger les propositions quand nécessaire
  useEffect(() => {
    const proposalsCount = currentMode === 'hardcore' ? 3 : 5;

    if (currentStepIndex === -1) {
      const allFormations = Object.values(draftFormations);
      const shuffled = [...allFormations].sort(() => Math.random() - 0.5);
      setFormationProposals(shuffled.slice(0, proposalsCount));
      setSelectedFormationTemp(null);
      // Précharger les images de la première étape joueurs
      setTimeout(() => preloadNextStep(0), 200);

    } else if (currentStepIndex >= 0 && currentStepIndex < DRAFT_CONFIG.length) {
      const currentConfig = DRAFT_CONFIG[currentStepIndex];
      let playersOfPosition;
      if (currentConfig.position === 'coach' || currentConfig.position === 'manager') {
        playersOfPosition = availablePlayers.filter(p =>
          !selectedTeam.players.some(s => s.id === p.id) &&
          p.id !== selectedTeam.coach?.id &&
          !selectedTeam.managers.some(m => m.id === p.id)
        );
      } else {
        playersOfPosition = availablePlayers.filter(p =>
          p.role === currentConfig.position &&
          !selectedTeam.players.some(s => s.id === p.id) &&
          p.id !== selectedTeam.coach?.id &&
          !selectedTeam.managers.some(m => m.id === p.id)
        );
      }
      const shuffled = [...playersOfPosition].sort(() => Math.random() - 0.5);
      const proposals = shuffled.slice(0, proposalsCount);
      // Précharger immédiatement les sprites courants
      preloadImages(proposals.map(p => p.sprite));
      setCurrentProposals(proposals);
      setSelectedPlayer(null);
      // Précharger spéculativement la prochaine étape
      setTimeout(() => preloadNextStep(currentStepIndex + 1), 400);

    } else if (currentStepIndex === 999) {
      const shuffled = [...availableEmblems].sort(() => Math.random() - 0.5);
      const proposals = shuffled.slice(0, proposalsCount);
      preloadImages(proposals.map(e => e.image));
      setCurrentProposals(proposals);
      setSelectedEmblem(null);
      // Précharger quelques maillots en avance
      setTimeout(() => {
        preloadImages([...availableKits].sort(() => Math.random() - 0.5).slice(0, 15).map(k => k.image));
      }, 400);

    } else if (currentStepIndex === 1000) {
      const shuffled = [...availableKits].sort(() => Math.random() - 0.5);
      const proposals = shuffled.slice(0, proposalsCount);
      preloadImages(proposals.map(k => k.image));
      setCurrentProposals(proposals);
      setSelectedKit(null);
    }
  }, [currentStepIndex, reloadTrigger, availablePlayers, selectedTeam, DRAFT_CONFIG, currentMode, availableEmblems, availableKits]);

  const startMode = (mode) => {
    setCurrentMode(mode);
    if (mode !== 'theme') {
      setCurrentStepIndex(-1);
    }
  };

  const startThemeDraft = (gameId) => {
    setSelectedGame(gameId);
    setCurrentStepIndex(-1);
    setFormationRefreshUsed(false);
  };

  const selectFormationTemp = (formationName) => {
    setSelectedFormationTemp(formationName);
  };

  const validateFormation = () => {
    if (!selectedFormationTemp) return;
    setSelectedFormation(selectedFormationTemp);
    resetDraft();
  };

  const refreshFormationProposals = () => {
    if (formationRefreshUsed || modeConfig.maxRefresh === 0) return;
    setFormationRefreshUsed(true);
    setReloadTrigger(prev => prev + 1); // Forcer le rechargement
  };

  const resetDraft = () => {
    setCurrentStepIndex(0);
    setCurrentSubStep(0);
    setSelectedTeam({
      coach: null,
      managers: [],
      players: [],
      emblem: null,
      kit: null
    });
    setRefreshUsed(false);
    setFormationRefreshUsed(false);
    setSelectedPlayer(null);
    setSelectedEmblem(null);
    setSelectedKit(null);
    setSelectedFormationTemp(null);
    setReloadTrigger(prev => prev + 1); // Forcer le rechargement
  };

  const refreshProposals = () => {
    if (refreshUsed || modeConfig.maxRefresh === 0) return;
    setRefreshUsed(true);
    setReloadTrigger(prev => prev + 1); // Forcer le rechargement
  };

  const selectPlayer = (player) => {
    setSelectedPlayer(player);
  };

  const selectEmblem = (emblem) => {
    setSelectedEmblem(emblem);
  };

  const selectKit = (kit) => {
    setSelectedKit(kit);
  };

  const validateChoice = () => {
    if (!selectedPlayer) return;
    
    const currentConfig = DRAFT_CONFIG[currentStepIndex];
    const newTeam = { ...selectedTeam };

    if (currentConfig.position === 'coach') {
      newTeam.coach = selectedPlayer;
    } else if (currentConfig.position === 'manager') {
      newTeam.managers = [...newTeam.managers, selectedPlayer];
    } else {
      newTeam.players = [...newTeam.players, selectedPlayer];
    }

    setSelectedTeam(newTeam);
    
    const newSubStep = currentSubStep + 1;
    
    if (newSubStep >= currentConfig.count) {
      const newStepIndex = currentStepIndex + 1;
      if (newStepIndex >= DRAFT_CONFIG.length) {
        setCurrentStepIndex(999);
        setRefreshUsed(false);
        setReloadTrigger(prev => prev + 1);
        return;
      }
      setCurrentStepIndex(newStepIndex);
      setCurrentSubStep(0);
    } else {
      setCurrentSubStep(newSubStep);
    }
    
    setRefreshUsed(false);
    setReloadTrigger(prev => prev + 1); // Forcer le rechargement des propositions
  };

  const validateEmblem = () => {
    if (!selectedEmblem) return;
    
    const newTeam = { ...selectedTeam };
    newTeam.emblem = selectedEmblem;
    setSelectedTeam(newTeam);
    
    setCurrentStepIndex(1000);
    setRefreshUsed(false);
    setReloadTrigger(prev => prev + 1);
  };

  const validateKit = () => {
    if (!selectedKit) return;
    
    const newTeam = { ...selectedTeam };
    newTeam.kit = selectedKit;
    setSelectedTeam(newTeam);
    
    setCurrentStepIndex(1001);
  };

  const exportTeam = () => {
    const byPosition = {
      'GK': selectedTeam.players.filter(p => p.role === 'GK'),
      'DF': selectedTeam.players.filter(p => p.role === 'DF'),
      'MF': selectedTeam.players.filter(p => p.role === 'MF'),
      'FW': selectedTeam.players.filter(p => p.role === 'FW')
    };
    
    let text = '=== ÉQUIPE INAZUMA ELEVEN ===\n\n';
    text += `Mode: ${modeConfig.name}`;
    if (selectedGame) {
      text += ` - ${selectedGame}`;
    }
    text += `\nFormation: ${selectedFormation}\n\n`;
    
    text += '👔 COACH:\n';
    text += `  - ${selectedTeam.coach?.name}\n`;
    
    text += '\n📋 MANAGERS:\n';
    selectedTeam.managers.forEach(m => text += `  - ${m.name}\n`);
    
    text += '\n🥅 GARDIEN:\n';
    byPosition.GK.forEach(p => text += `  - ${p.name}\n`);
    
    text += '\n🛡️ DÉFENSEURS:\n';
    byPosition.DF.forEach(p => text += `  - ${p.name}\n`);
    
    text += '\n⚡ MILIEUX:\n';
    byPosition.MF.forEach(p => text += `  - ${p.name}\n`);
    
    text += '\n⚔️ ATTAQUANTS:\n';
    byPosition.FW.forEach(p => text += `  - ${p.name}\n`);

    text += '\n🛡️ EMBLÈME:\n';
    text += `  - ${selectedTeam.emblem?.name}\n`;

    text += '\n👕 MAILLOT:\n';
    text += `  - ${selectedTeam.kit?.name}\n`;

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'equipe_inazuma.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const goHome = () => {
    setCurrentMode(null);
    setSelectedGame(null);
    setSelectedFormation(null);
    resetDraft();
  };

  const allPlayersSelected = selectedTeam.players.length === DRAFT_CONFIG.reduce((sum, c) => c.position !== 'coach' && c.position !== 'manager' ? sum + c.count : sum, 0);
  const isDraftComplete = allPlayersSelected && selectedTeam.emblem && selectedTeam.kit;
  const currentConfig = DRAFT_CONFIG[currentStepIndex] || {};

  // Fonction pour obtenir la couleur du badge selon le rôle
  const getBadgeColor = (role) => {
    switch(role) {
      case 'GK': return 'bg-yellow-600';
      case 'DF': return 'bg-blue-600';
      case 'MF': return 'bg-green-600';
      case 'FW': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  // Fonction pour obtenir l'image de fond de chaque jeu
  const getGameBackground = (game) => {
    const backgrounds = {
      'Inazuma Eleven 1': '/games/ie1.jpg',
      'Inazuma Eleven 2': '/games/ie2.jpg',
      'Inazuma Eleven 3': '/games/ie3.jpg',
      'Inazuma Eleven GO': '/games/iego.jpg',
      'Inazuma Eleven GO 2': '/games/iego2.jpg',
      'Inazuma Eleven GO Galaxy': '/games/iego3.jpg',
      'Inazuma Eleven Ares': '/games/ieares.jpg',
      'Inazuma Eleven Orion': '/games/ieorion.jpg',
      'Inazuma Eleven Victory Road': '/games/ievr.jpg'
    };
    return backgrounds[game] || null;
  };

  // ÉCRAN 1 : Sélection du mode
  if (!currentMode) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8 text-center">
          <h1 className="text-2xl sm:text-4xl font-bold text-indigo-600 mb-2 sm:mb-4">⚽ Draft Inazuma Eleven</h1>
          <p className="text-base sm:text-xl text-gray-600">Choisissez votre mode de draft</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <button
            onClick={() => startMode('classic')}
            className="bg-white rounded-lg shadow-lg p-5 sm:p-8 text-left hover:shadow-xl transition-all transform hover:-translate-y-1 border-t-4 border-green-500"
          >
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">Mode Classique</h2>
            <p className="text-gray-600 mb-4">Le mode standard pour une expérience équilibrée</p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> 5 propositions par étape
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> 1 rafraîchissement autorisé
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> 1 emblème et 1 maillot
              </li>
            </ul>
          </button>

          <button
            onClick={() => startMode('hardcore')}
            className="bg-white rounded-lg shadow-lg p-8 text-left hover:shadow-xl transition-all transform hover:-translate-y-1 border-t-4 border-red-500"
          >
            <div className="text-6xl mb-4">💀</div>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">Mode Hardcore</h2>
            <p className="text-gray-600 mb-4">Pour les vrais experts ! Aucun rafraîchissement</p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <span className="text-red-500">⚠</span> 3 propositions par étape
              </li>
              <li className="flex items-center gap-2">
                <span className="text-red-500">⚠</span> Aucun rafraîchissement
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> 1 emblème et 1 maillot
              </li>
            </ul>
          </button>

          <button
            onClick={() => startMode('theme')}
            className="bg-white rounded-lg shadow-lg p-8 text-left hover:shadow-xl transition-all transform hover:-translate-y-1 border-t-4 border-purple-500"
          >
            <div className="text-6xl mb-4">🎨</div>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">Mode Thème</h2>
            <p className="text-gray-600 mb-4">Une équipe d'un seul jeu de la saga</p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> 5 propositions par étape
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> 1 rafraîchissement autorisé
              </li>
              <li className="flex items-center gap-2">
                <span className="text-purple-500">★</span> Joueurs/emblème/maillot du même jeu
              </li>
            </ul>
          </button>
        </div>
      </div>
    );
  }

  // ÉCRAN 2 : Sélection du jeu (mode thème uniquement)
  if (currentMode === 'theme' && !selectedGame) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-center mb-6">🎨 Choisissez votre jeu</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {gamesList.filter(g => g !== 'Tous les jeux').map(game => {
              const bgImage = getGameBackground(game);
              return (
                <button
                  key={game}
                  onClick={() => startThemeDraft(game)}
                  className="relative overflow-hidden rounded-lg hover:shadow-xl transition-all transform hover:scale-105 h-40"
                  style={{
                    backgroundImage: bgImage ? `url(${bgImage})` : 'linear-gradient(to bottom right, rgb(168, 85, 247), rgb(219, 39, 119))',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Overlay sombre pour améliorer la lisibilité du texte */}
                  <div className="absolute inset-0 bg-black bg-opacity-40 hover:bg-opacity-30 transition-all" />
                  
                  {/* Texte du jeu */}
                  <div className="relative z-10 h-full flex items-center justify-center p-6">
                    <h3 className="text-xl font-bold text-white text-center drop-shadow-lg">
                      {game}
                    </h3>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ÉCRAN 3 : Sélection de la formation
  if (currentStepIndex === -1) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-lg p-3 sm:p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg sm:text-2xl font-bold mb-1">📐 Choisissez votre formation</h2>
              <p className="text-indigo-100 text-sm sm:text-base">
                {modeConfig.proposalsCount} propositions - 1 à choisir
              </p>
            </div>
            <div className="text-3xl sm:text-5xl">📐</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-center">
            Choisissez parmi ces propositions :
          </h3>
          
          <div className={`grid gap-3 mb-6 ${
            modeConfig.proposalsCount === 5 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3'
          }`}>
            {formationProposals.map((formation) => {
              const isSelected = selectedFormationTemp === formation.name;
              return (
                <div
                  key={formation.name}
                  onClick={() => selectFormationTemp(formation.name)}
                  className={`cursor-pointer rounded-lg p-3 sm:p-6 transition-all transform hover:-translate-y-1 ${
                    isSelected
                      ? 'bg-green-100 border-2 border-green-500 shadow-lg'
                      : 'bg-gray-50 border-2 border-gray-200 hover:border-indigo-500 hover:shadow-md'
                  }`}
                >
                  <h3 className="text-xl font-bold mb-3 text-center">{formation.name}</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="flex justify-between">
                      <span>Défenseurs:</span>
                      <span className="font-bold text-blue-600">{formation.positions.DF}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Milieux:</span>
                      <span className="font-bold text-green-600">{formation.positions.MF}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Attaquants:</span>
                      <span className="font-bold text-red-600">{formation.positions.FW}</span>
                    </p>
                  </div>
                  {isSelected && (
                    <div className="mt-3 text-center text-green-600 font-bold text-sm">
                      ✓ CHOISIE
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={refreshFormationProposals}
              disabled={formationRefreshUsed || modeConfig.maxRefresh === 0}
              className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
                formationRefreshUsed || modeConfig.maxRefresh === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:shadow-lg'
              }`}
            >
              <RotateCcw size={18} />
              {modeConfig.maxRefresh === 0 ? 'Refresh désactivé' : formationRefreshUsed ? 'Refresh utilisé' : 'Nouvelles propositions'}
            </button>

            <button
              onClick={validateFormation}
              disabled={!selectedFormationTemp}
              className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
                !selectedFormationTemp
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg'
              }`}
            >
              Valider la formation →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ÉCRAN 4 : Sélection de l'emblème
  if (currentStepIndex === 999) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-lg p-3 sm:p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg sm:text-2xl font-bold mb-1 flex items-center gap-2">
                <Shield size={24} />
                Choisissez votre emblème
              </h2>
              <p className="text-indigo-100 text-sm sm:text-base">
                {modeConfig.proposalsCount} propositions - 1 à choisir
              </p>
            </div>
            <div className="hidden sm:block"><Shield size={40} /></div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-center">
            Choisissez parmi ces propositions :
          </h3>
          
          <div className={`grid gap-3 mb-6 ${
            modeConfig.proposalsCount === 5 ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3'
          }`}>
            {currentProposals.map((emblem) => {
              const isSelected = selectedEmblem?.id === emblem.id;
              return (
                <div
                  key={emblem.id}
                  onClick={() => selectEmblem(emblem)}
                  className={`cursor-pointer rounded-lg p-2 sm:p-4 transition-all transform hover:-translate-y-1 ${
                    isSelected
                      ? 'bg-green-100 border-2 border-green-500 shadow-lg'
                      : 'bg-gray-50 border-2 border-gray-200 hover:border-indigo-500 hover:shadow-md'
                  }`}
                >
                  <div className="bg-gray-100 w-full aspect-square rounded mb-2 sm:mb-3 flex items-center justify-center overflow-hidden">
                    <img 
                      src={emblem.image} 
                      alt={emblem.name}
                      className="w-full h-full object-contain"
                      loading="eager"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div style={{display: 'none'}} className="w-full h-full flex items-center justify-center">
                      <Shield size={40} className="text-indigo-400" />
                    </div>
                  </div>
                  <div className="font-bold text-xs sm:text-sm mb-1 sm:mb-2 text-center leading-tight">{emblem.name}</div>
                  {isSelected && (
                    <div className="mt-2 text-center text-green-600 font-bold text-sm">
                      ✓ CHOISI
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={refreshProposals}
              disabled={refreshUsed || modeConfig.maxRefresh === 0}
              className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
                refreshUsed || modeConfig.maxRefresh === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:shadow-lg'
              }`}
            >
              <RotateCcw size={18} />
              {modeConfig.maxRefresh === 0 ? 'Refresh désactivé' : refreshUsed ? 'Refresh utilisé' : 'Nouvelles propositions'}
            </button>

            <button
              onClick={validateEmblem}
              disabled={!selectedEmblem}
              className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
                !selectedEmblem
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg'
              }`}
            >
              Valider l'emblème →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ÉCRAN 5 : Sélection du maillot
  if (currentStepIndex === 1000) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-lg p-3 sm:p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg sm:text-2xl font-bold mb-1 flex items-center gap-2">
                <Shirt size={24} />
                Choisissez votre maillot
              </h2>
              <p className="text-indigo-100 text-sm sm:text-base">
                {modeConfig.proposalsCount} propositions - 1 à choisir
              </p>
            </div>
            <div className="hidden sm:block"><Shirt size={40} /></div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-center">
            Choisissez parmi ces propositions :
          </h3>
          
          <div className={`grid gap-3 mb-6 ${
            modeConfig.proposalsCount === 5 ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5' : 'grid-cols-2 sm:grid-cols-3'
          }`}>
            {currentProposals.map((kit) => {
              const isSelected = selectedKit?.id === kit.id;
              return (
                <div
                  key={kit.id}
                  onClick={() => selectKit(kit)}
                  className={`cursor-pointer rounded-lg p-2 sm:p-4 transition-all transform hover:-translate-y-1 ${
                    isSelected
                      ? 'bg-green-100 border-2 border-green-500 shadow-lg'
                      : 'bg-gray-50 border-2 border-gray-200 hover:border-indigo-500 hover:shadow-md'
                  }`}
                >
                  <div className="w-full aspect-square rounded mb-2 sm:mb-3 overflow-hidden">
                    <img 
                      src={kit.image} 
                      alt={kit.name}
                      className="w-full h-full object-contain"
                      loading="eager"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div 
                      style={{ 
                        display: 'none',
                        background: `linear-gradient(135deg, ${kit.primary} 50%, ${kit.secondary} 50%)`
                      }}
                      className="w-full h-full"
                    />
                  </div>
                  <div className="font-bold text-xs sm:text-sm mb-1 sm:mb-2 text-center leading-tight">{kit.name}</div>
                  {isSelected && (
                    <div className="mt-1 sm:mt-2 text-center text-green-600 font-bold text-xs sm:text-sm">
                      ✓ CHOISI
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={refreshProposals}
              disabled={refreshUsed || modeConfig.maxRefresh === 0}
              className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
                refreshUsed || modeConfig.maxRefresh === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:shadow-lg'
              }`}
            >
              <RotateCcw size={18} />
              {modeConfig.maxRefresh === 0 ? 'Refresh désactivé' : refreshUsed ? 'Refresh utilisé' : 'Nouvelles propositions'}
            </button>

            <button
              onClick={validateKit}
              disabled={!selectedKit}
              className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
                !selectedKit
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:shadow-lg'
              }`}
            >
              Terminer le Draft ! 🏆
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ÉCRAN 6 : Draft terminé
  if (currentStepIndex === 1001 || isDraftComplete) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🏆</div>
          <h1 className="text-4xl font-bold text-indigo-600 mb-2">Draft Terminé !</h1>
          <p className="text-xl text-gray-600">Votre équipe est complète</p>
          <p className="text-sm text-gray-500 mt-2">Formation : {selectedFormation}</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6">Votre Équipe Finale</h2>
          
          {/* Coach */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3">👔 Coach</h3>
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-yellow-500 inline-block">
              <div className="flex justify-center mb-2">
                <PlayerAvatar player={selectedTeam.coach} size="w-16 h-16" />
              </div>
              <p className="font-bold text-center">{selectedTeam.coach?.name}</p>
            </div>
          </div>

          {/* Managers */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3">📋 Managers</h3>
            <div className="grid grid-cols-3 gap-4">
              {selectedTeam.managers.map((manager, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <div className="flex justify-center mb-2">
                    <PlayerAvatar player={manager} size="w-16 h-16" />
                  </div>
                  <p className="font-bold text-center text-sm">{manager.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Joueurs */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3">⚽ Équipe</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {selectedTeam.players.map((player, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4 border-l-4 border-green-500">
                  <div className="flex justify-center mb-2">
                    <PlayerAvatar player={player} size="w-16 h-16" />
                  </div>
                  <p className="font-bold text-center text-sm">{player.name}</p>
                  <p className="text-xs text-gray-600 text-center">{player.role}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Emblème */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Shield /> Emblème
            </h3>
            <div className="bg-gray-100 p-4 rounded-lg inline-block">
              <div className="w-20 h-20 mb-2 mx-auto flex items-center justify-center">
                <img 
                  src={selectedTeam.emblem?.image} 
                  alt={selectedTeam.emblem?.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div style={{display: 'none'}} className="w-full h-full flex items-center justify-center">
                  <Shield size={60} className="text-indigo-600" />
                </div>
              </div>
              <p className="text-sm text-center font-medium">{selectedTeam.emblem?.name}</p>
            </div>
          </div>

          {/* Maillot */}
          <div>
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Shirt /> Maillot
            </h3>
            <div className="bg-gray-100 p-4 rounded-lg inline-block">
              <div className="w-20 h-20 rounded mb-2 mx-auto overflow-hidden">
                <img 
                  src={selectedTeam.kit?.image} 
                  alt={selectedTeam.kit?.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div 
                  style={{ 
                    display: 'none',
                    background: `linear-gradient(135deg, ${selectedTeam.kit?.primary} 50%, ${selectedTeam.kit?.secondary} 50%)`
                  }}
                  className="w-full h-full"
                />
              </div>
              <p className="text-sm text-center font-medium">{selectedTeam.kit?.name}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
          <button
            onClick={() => {
              if (onImportTeam) {
                onImportTeam(selectedTeam.players);
              } else {
                alert('⚠️ La fonction d\'import n\'est pas disponible.');
              }
            }}
            className="px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Users size={18} />
            Envoyer vers l'Équipe
          </button>
          <button
            onClick={exportTeam}
            className="px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Download size={18} />
            Exporter l'équipe
          </button>
          <button
            onClick={goHome}
            className="px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Home size={18} />
            Nouveau draft
          </button>
        </div>
      </div>
    );
  }

  // ÉCRAN PRINCIPAL : Draft en cours avec propositions
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-lg p-3 sm:p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg sm:text-2xl font-bold mb-1">{currentConfig.label}</h2>
            <p className="text-indigo-100 text-sm sm:text-base">
              {currentSubStep + 1} / {currentConfig.count}
              {currentMode === 'theme' && ` - ${selectedGame}`}
              {` - Formation: ${selectedFormation}`}
            </p>
          </div>
          <div className="text-3xl sm:text-5xl">{currentConfig.label.split(' ')[0]}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
        <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-center">
          Choisissez parmi ces propositions :
        </h3>
        
        {currentProposals.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-red-600 font-bold text-xl mb-4">⚠️ Aucune proposition disponible</p>
            <button 
              onClick={() => setReloadTrigger(prev => prev + 1)}
              className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Recharger les propositions
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {currentProposals.map((player) => {
              const isSelected = selectedPlayer?.id === player.id;
              return (
                <div
                  key={player.id}
                  onClick={() => selectPlayer(player)}
                  className={`cursor-pointer rounded-lg p-2 sm:p-4 transition-all transform hover:-translate-y-1 ${
                    isSelected
                      ? 'bg-green-100 border-2 border-green-500 shadow-lg'
                      : 'bg-gray-50 border-2 border-gray-200 hover:border-indigo-500 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-center mb-2 sm:mb-3">
                    <PlayerAvatar player={player} size="w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24" />
                  </div>
                  <div className="font-bold text-xs sm:text-sm mb-1 sm:mb-2 text-center truncate">{player.name}</div>
                  <div className="flex gap-1 justify-center flex-wrap">
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-bold text-white ${getBadgeColor(player.role)}`}>
                      {player.role}
                    </span>
                  </div>
                  {isSelected && (
                    <div className="mt-1 sm:mt-2 text-center text-green-600 font-bold text-xs sm:text-sm">
                      ✓ CHOISI
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={refreshProposals}
            disabled={refreshUsed || modeConfig.maxRefresh === 0}
            className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
              refreshUsed || modeConfig.maxRefresh === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:shadow-lg'
            }`}
          >
            <RotateCcw size={18} />
            {modeConfig.maxRefresh === 0 ? 'Refresh désactivé' : refreshUsed ? 'Refresh utilisé' : 'Nouvelles propositions'}
          </button>

          <button
            onClick={validateChoice}
            disabled={!selectedPlayer}
            className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
              !selectedPlayer
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg'
            }`}
          >
            Valider le choix →
          </button>
        </div>
      </div>

      {/* Équipe en cours */}
      {(selectedTeam.coach || selectedTeam.managers.length > 0 || selectedTeam.players.length > 0) && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4">📋 Sélection en cours</h3>
          
          {selectedTeam.coach && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-600 mb-2">Coach:</p>
              <div className="inline-block bg-yellow-50 border-l-4 border-yellow-500 p-2 rounded">
                <span className="text-sm font-bold">{selectedTeam.coach.name}</span>
              </div>
            </div>
          )}

          {selectedTeam.managers.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-600 mb-2">Managers ({selectedTeam.managers.length}/3):</p>
              <div className="flex gap-2 flex-wrap">
                {selectedTeam.managers.map((m, i) => (
                  <div key={i} className="bg-blue-50 border-l-4 border-blue-500 p-2 rounded">
                    <span className="text-sm font-bold">{m.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedTeam.players.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-2">Joueurs ({selectedTeam.players.length}/11):</p>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {selectedTeam.players.map((player, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 border-l-4 border-green-500">
                    <div className="flex justify-center mb-2">
                      <PlayerAvatar player={player} size="w-12 h-12" />
                    </div>
                    <div className="text-xs font-bold text-center truncate">{player.name}</div>
                    <div className="flex gap-1 justify-center mt-1">
                      <span className={`px-1 py-0.5 rounded text-xs text-white ${getBadgeColor(player.role)}`}>
                        {player.role}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Draft;
